import { CurrentSubscription, SubscriptionPackage } from '~/subscription/types/subscription.types'
import { getTrialPeriodInDaysForUser } from '~/subscription/util'
import getStripePriceCurrencyInterval from '~/subscription/getStripePriceCurrencyInterval'
import { BookingCurrency } from '~/types/booking'
import useStripePrice from '~/subscription/hooks/useStripePrice'
import { useHasUserAvailedFreemium } from '~/redux/utils/subscription'

export default function useSubscriptionPackageFormatter(
  filterBy: 'interval' | 'packageType' = 'interval',
  coupon?: string,
): {
  freePackage: SubscriptionPackage
  packages: SubscriptionPackage[]
  currentSubscriptionInfo?: CurrentSubscription
  arePricesSettled: boolean
} {
  const discardTrial = useHasUserAvailedFreemium()
  const pxCxs = 'px-4 md:px-8 xl:px-12'
  const {
    freePrice,
    recommendedPrice,
    allPricesData,
    currentSubscriptionPrice,
    currentSubscription,
    arePricesSettled,
  } = useStripePrice(filterBy, coupon)

  const { stripePaymentInterval: freeStripePaymentInterval } = getStripePriceCurrencyInterval(
    {
      price: (recommendedPrice?.unit_amount || recommendedPrice?.tiers?.[0]?.unit_amount) as number,
      currency: recommendedPrice?.currency as BookingCurrency,
      interval: recommendedPrice?.recurring?.interval as string,
      intervalCount: recommendedPrice?.recurring?.interval_count as number,
      coupon: recommendedPrice?.coupon,
    },
    {},
    '',
  )
  const freePackage: SubscriptionPackage = {
    className: pxCxs,
    currency: recommendedPrice?.currency as BookingCurrency,
    currencyOptions: recommendedPrice?.currency_options,
    price: 0,
    priceId: freePrice?.id,
    subscriptionPlanItems: freePrice?.product?.metadata || {},
    stripePaymentInterval: freeStripePaymentInterval,
    priceApiIsLoading: !freePrice,
    fullPrice: 0,
    freeTrialPeriod: getTrialPeriodInDaysForUser(freePrice, currentSubscription),
    durationInMonths: 1,
    isBestValue: false,
    productName: freePrice?.product?.name,
    productId: freePrice?.product?.id,
    isFamilyPrice: freePrice?.metadata?.subscriptionType === 'family',
  }

  const { fullPrice: currentSubscriptionFullprice, stripePriceAmount: currentPrice } = getStripePriceCurrencyInterval(
    {
      price: (currentSubscriptionPrice?.unit_amount as number) ?? currentSubscriptionPrice?.tiers?.[0]?.unit_amount,
      currency: currentSubscriptionPrice?.currency as BookingCurrency,
      interval: currentSubscriptionPrice?.recurring?.interval as string,
      intervalCount: currentSubscriptionPrice?.recurring?.interval_count as number,
      coupon: currentSubscription?.discount?.coupon,
    },
    {},
    '',
  )

  const currentSubscriptionProductId = currentSubscriptionPrice?.product?.id
  const currentSubscriptionInfo = currentSubscriptionPrice
    ? {
        productName: currentSubscriptionPrice?.product?.name,
        priceId: currentSubscriptionPrice?.id,
        productId: currentSubscriptionPrice?.product?.id,
        fullPrice: currentSubscriptionFullprice,
        currency: currentSubscription?.currency || '',
        interval: currentSubscriptionPrice?.recurring?.interval || '',
        paymentGateway: currentSubscription?.metadata?.paymentGateway || '',
        coupon: currentSubscription?.discount?.coupon,
        price: currentPrice,
        isFamilySubscription: currentSubscription?.metadata?.subscriptionType === 'family',
      }
    : undefined

  const packages: SubscriptionPackage[] = allPricesData?.map((data: any): SubscriptionPackage => {
    const stripePriceCurrencyIntervalParameters =
      data?.product?.id !== currentSubscriptionProductId
        ? {
            price: data?.unit_amount || data?.tiers?.[0]?.unit_amount,
            licensePrice: data?.tiers?.[1]?.unit_amount ?? 0,
            currency: data?.currency,
            interval: data?.recurring?.interval,
            intervalCount: data?.recurring?.interval_count,
            coupon: data?.coupon,
          }
        : {
            price: currentSubscriptionPrice?.unit_amount || currentSubscriptionPrice?.tiers?.[0]?.unit_amount,
            licensePrice: currentSubscriptionPrice?.tiers?.[1]?.unit_amount ?? 0,
            currency: currentSubscriptionPrice?.currency,
            interval: currentSubscriptionPrice?.recurring?.interval,
            intervalCount: currentSubscriptionPrice?.recurring?.interval_count,
            coupon: currentSubscriptionPrice?.coupon,
          }

    const {
      stripePriceAmount: stripePriceAmountTemp,
      stripePaymentInterval: stripePaymentIntervalTemp,
      fullPrice: fullPriceTemp,
      licensePrice: licensePriceTemp,
      durationInMonths: durationInMonthsTemp,
    } = getStripePriceCurrencyInterval(
      stripePriceCurrencyIntervalParameters,
      data.currency_options,
      currentSubscription?.currency as BookingCurrency,
    )

    return {
      className: pxCxs,
      currency: data.currency,
      currencyOptions: data.currency_options,
      price: stripePriceAmountTemp,
      priceId: data.id,
      subscriptionPlanItems: data?.product?.metadata || {},
      stripePaymentInterval: stripePaymentIntervalTemp,
      priceApiIsLoading: !data,
      fullPrice: fullPriceTemp,
      licensePrice: licensePriceTemp,
      freeTrialPeriod: !discardTrial ? getTrialPeriodInDaysForUser(data, currentSubscription) : 0,
      durationInMonths: durationInMonthsTemp as number,
      isBestValue: false,
      productName: data?.product?.name,
      productId: data?.product?.id,
      isVippsSubscription: Boolean(currentSubscription?.metadata?.vippsAgreementId),
      isFamilyPrice: data?.metadata?.subscriptionType === 'family',
    }
  })

  const sortedPackages = packages.toSorted((a, b) => {
    if (a.isFamilyPrice === b.isFamilyPrice) return 0
    return a.isFamilyPrice ? -1 : 1
  })

  return {
    freePackage,
    packages: sortedPackages,
    currentSubscriptionInfo,
    arePricesSettled,
  }
}
